// Registration service — single clear entry point so the form can later connect
// to GoHighLevel / CRM / email automation without touching UI code.
import axios from "axios";
import { logger } from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JSON_HEADERS = { headers: { "Content-Type": "application/json" } };

export async function submitRegistration(payload) {
  // payload: { full_name, mobile, email, age_range, biggest_question,
  //            invited_by, marketing_consent, attribution, event_id,
  //            meta_event_id, fbp, fbc, page_url }
  const res = await axios.post(`${API}/registrations`, payload, JSON_HEADERS);
  return res.data;
}

// Exchange the dashboard password for a short-lived opaque session token.
// Only the token is kept in the browser; the password never persists.
export async function adminLogin(password) {
  const res = await axios.post(`${API}/admin/login`, { password }, JSON_HEADERS);
  return res.data; // { token, expires_at }
}

// Token-protected admin fetch for the Registrations Dashboard.
export async function fetchRegistrations(token, eventId) {
  const res = await axios.post(`${API}/admin/registrations`, { token, event_id: eventId }, JSON_HEADERS);
  return res.data;
}

// Public, live seat availability (capacity / taken / remaining).
export async function fetchSeats(eventId) {
  const res = await axios.get(`${API}/seats`, { params: eventId ? { event_id: eventId } : {} });
  return res.data;
}

// Token-protected admin action to delete registrations.
export async function clearRegistrations(token, eventId) {
  const res = await axios.post(`${API}/admin/registrations/clear`, { token, event_id: eventId }, JSON_HEADERS);
  return res.data;
}

// Best-effort: record a landing-page visit for link-traffic analytics.
export async function trackVisit({ referral_code, visitor_id, event_id, landing_page_url, referrer, utm_source }) {
  try {
    await axios.post(
      `${API}/track/visit`,
      { referral_code, visitor_id, event_id, landing_page_url, referrer, utm_source },
      JSON_HEADERS
    );
  } catch (e) {
    // Visit tracking is best-effort and must never block the page load.
    logger.debug("[trackVisit] failed", e);
  }
}

// Token-protected admin action to reset link traffic (visit) data.
export async function clearTraffic(token, eventId) {
  const res = await axios.post(`${API}/admin/traffic/clear`, { token, event_id: eventId }, JSON_HEADERS);
  return res.data;
}

// Token-protected admin action to delete a single registration (e.g. test entries).
export async function deleteRegistration(token, regId, eventId) {
  const res = await axios.post(
    `${API}/admin/registrations/delete`,
    { token, reg_id: regId, event_id: eventId },
    JSON_HEADERS
  );
  return res.data; // { deleted, trash_id }
}

// Token-protected admin action to delete several registrations at once.
export async function deleteRegistrationsMany(token, regIds, eventId) {
  const res = await axios.post(
    `${API}/admin/registrations/delete-many`,
    { token, reg_ids: regIds, event_id: eventId },
    JSON_HEADERS
  );
  return res.data; // { deleted, trash_id }
}

// Token-protected undo: restore a just-deleted batch by its trash id.
export async function restoreRegistrations(token, trashId) {
  const res = await axios.post(
    `${API}/admin/registrations/restore`,
    { token, trash_id: trashId },
    JSON_HEADERS
  );
  return res.data; // { restored }
}

// Token-protected: resend a reminder email to specific registrants now.
export async function resendReminders(token, regIds, milestone, eventId) {
  const res = await axios.post(
    `${API}/admin/reminders/resend`,
    { token, reg_ids: regIds, milestone, event_id: eventId },
    JSON_HEADERS
  );
  return res.data; // { mode, milestone, attempted, sent }
}
